import { ROUTES } from '../../constants/routes';
import { PERMISSION } from '../../shared/utils/permissions';

export interface SectionTab {
  /** Path segment under /dashboard; each tab keeps its own URL. */
  route: string;
  permission: string;
  icon: string;
  labelKey: string;
  /**
   * Platform staff only: hidden from anyone limited to an organization, even
   * with the permission (an org owner holds organizations.view to list their
   * own organization and stations.view for their stations, but neither the
   * platform's organizations nor its support desk is theirs).
   */
  platformOnly?: boolean;
}

export interface TabVisibilityContext {
  has: (permission: string) => boolean;
  organization: { id: string } | null;
}

export const canViewTab = (tab: SectionTab, { has, organization }: TabVisibilityContext): boolean =>
  has(tab.permission) && !(tab.platformOnly && organization);

/** Sibling pages shown as tabs under one sidebar entry. */
export interface DashboardSection {
  labelKey: string;
  titleKey: string;
  icon: string;
  /** The first tab is where the sidebar entry lands. */
  tabs: readonly SectionTab[];
}

export const ORGANIZATIONS_ROLES_SECTION: DashboardSection = {
  labelKey: 'dashboard.sidebar.organizationsRoles',
  titleKey: 'dashboard.pageTitles.organizationsRoles',
  icon: 'corporate_fare',
  tabs: [
    { route: ROUTES.DASH_ORGANIZATIONS, permission: PERMISSION.ORGANIZATIONS_VIEW, icon: 'corporate_fare', labelKey: 'dashboard.sidebar.organizations', platformOnly: true },
    { route: ROUTES.DASH_ROLES, permission: PERMISSION.ROLES_VIEW, icon: 'admin_panel_settings', labelKey: 'dashboard.sidebar.roles' },
  ],
};

export const SUPPORT_SECTION: DashboardSection = {
  labelKey: 'dashboard.sidebar.supportSection',
  titleKey: 'dashboard.pageTitles.supportSection',
  icon: 'support_agent',
  tabs: [
    { route: ROUTES.DASH_SUPPORT, permission: PERMISSION.SUPPORT_VIEW, icon: 'forum', labelKey: 'dashboard.sidebar.support', platformOnly: true },
    { route: ROUTES.DASH_KNOWLEDGE_BASE, permission: PERMISSION.SUPPORT_VIEW, icon: 'menu_book', labelKey: 'dashboard.sidebar.knowledgeBase', platformOnly: true },
    { route: ROUTES.DASH_STATION_REQUESTS, permission: PERMISSION.STATIONS_VIEW, icon: 'add_location_alt', labelKey: 'dashboard.sidebar.stationRequests', platformOnly: true },
  ],
};

/** The tab a path belongs to, matched on its first segment under /dashboard. */
export const sectionTabForPath = (section: DashboardSection, pathname: string) => {
  const segment = pathname.split('/')[2];
  return section.tabs.find((tab) => tab.route === segment);
};
